import * as React from "react"
import { PaintbrushMap, PaintbrushMapRef } from "./paintbrush_map"
import { PaintbrushToolbar } from "./paintbrush_toolbar"
import { PaintbrushSidebar } from "./paintbrush_sidebar"
import { PaintClass, PaintStyle, ToolMode } from "./utils/types"
import { LayerPalette } from "../projects/editor/layer_palette"
import { DBModels } from "../projects/state/db_models"
import { DatasetLayer, Layer } from "../projects/state"
import { CompiledDatasetRecord, getDataset, getDatasets, getTeamData } from "../projects/state/saved_dataset"
import { ProjectPermissions } from "../projects/editor/project_editor"
import { BooleanTileGrid, CategoricalTileGrid, tileGridStats } from "../projects/modelling/tile_grid"
import type { LocalGeotiffLayerState } from "../projects/map/local_geotiff_preview"
import {
  omitPaintbrushLocalGeotiffsFromLayerOrder,
  paintbrushLocalGeotiffOrderId,
  parsePaintbrushLocalGeotiffOrderId,
} from "./local_geotiff_constants"
import {
  buildImportClassByValueFromGrid,
  DEFAULT_CLASS_PALETTE,
} from "./load_classes_from_layer"

type AddedProjectLayer = {
  id: string
  layer: Layer
}
const LANDSCAPES_V2_API = "https://landscapes.wearepal.ai/api/v2"
const GEOTIFF_TO_SHP_SMOOTH = 0
const GEOTIFF_TO_SHP_SMOOTH_ITERS = 0
const PAINTBRUSH_BROADCAST_CHANNEL = "landscapes:paintbrush-updates"

function hexToRgb(color: string): [number, number, number] {
  const normalized = color.replace("#", "")
  const full = normalized.length === 3
    ? normalized.split("").map(c => `${c}${c}`).join("")
    : normalized
  const intValue = Number.parseInt(full, 16)
  if (Number.isNaN(intValue)) return [128, 128, 128]
  return [
    (intValue >> 16) & 255,
    (intValue >> 8) & 255,
    intValue & 255
  ]
}

function generateClrFromClassMap(
  labels: Record<string, unknown> | undefined,
  classByValue: Record<number, { classId: number; color: string }>
): string {
  const lines: string[] = []

  if (labels && Object.keys(labels).length > 0) {
    Object.entries(labels)
      .map(([key, label]) => [Number.parseInt(key, 10), String(label ?? key)] as const)
      .filter(([value]) => Number.isFinite(value))
      .sort(([a], [b]) => a - b)
      .forEach(([value, label]) => {
        const classInfo = classByValue[value]
        if (!classInfo) return
        const [r, g, b] = hexToRgb(classInfo.color)
        lines.push(`${value} ${r} ${g} ${b} 255 ${label}`)
      })
  } else {
    Object.keys(classByValue)
      .map(value => Number.parseInt(value, 10))
      .filter(value => Number.isFinite(value))
      .sort((a, b) => a - b)
      .forEach((value) => {
        const classInfo = classByValue[value]
        const [r, g, b] = hexToRgb(classInfo.color)
        lines.push(`${value} ${r} ${g} ${b} 255 class_${value}`)
      })
  }

  return lines.join("\n")
}

async function parseShapefileZip(zipArrayBuffer: ArrayBuffer): Promise<unknown> {
  const candidates: unknown[] = []
  candidates.push((window as unknown as Record<string, unknown>).shp)

  try {
    // Webpacker/CJS interop fallback
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const required = require("shpjs/dist/shp.js")
    candidates.push(required)
    candidates.push(required?.default)
    candidates.push(required?.parseZip)
  } catch {
    // ignore; fallback paths above may still work
  }

  const resolveCandidateFn = (value: unknown, depth = 0): ((input: ArrayBuffer) => Promise<unknown>) | null => {
    if (!value || depth > 3) return null
    if (typeof value === "function") {
      return value as (input: ArrayBuffer) => Promise<unknown>
    }
    if (typeof value === "object") {
      const obj = value as Record<string, unknown>
      if (typeof obj.parseZip === "function") return obj.parseZip as (input: ArrayBuffer) => Promise<unknown>
      if (typeof obj.default === "function") return obj.default as (input: ArrayBuffer) => Promise<unknown>
      if (obj.default) {
        const nested = resolveCandidateFn(obj.default, depth + 1)
        if (nested) return nested
      }
    }
    return null
  }

  for (const candidate of candidates) {
    const parserFn = resolveCandidateFn(candidate)
    if (parserFn) {
      return await parserFn(zipArrayBuffer)
    }
  }

  throw new Error("Unable to initialize shapefile parser in this browser build.")
}

type PaintbrushAppProps = {
  annotationId: number
  annotationName: string
  teamId: number
  teamName: string
  backButtonPath: string
  geojsonUrl?: string
  permissions: ProjectPermissions
  initialLayerState?: Record<string, unknown>
}

export const PaintbrushApp: React.FC<PaintbrushAppProps> = ({
  annotationId,
  annotationName,
  teamId,
  teamName,
  backButtonPath,
  geojsonUrl,
  permissions,
  initialLayerState = {}
}) => {
  const broadcastUpdate = React.useCallback(() => {
    if (typeof BroadcastChannel === "undefined") return
    const channel = new BroadcastChannel(PAINTBRUSH_BROADCAST_CHANNEL)
    channel.postMessage({
      type: "annotation-updated",
      annotationId,
      teamId,
      updatedAt: Date.now()
    })
    channel.close()
  }, [annotationId, teamId])

  const persistedProjectLayers = Array.isArray(initialLayerState.projectLayers)
    ? initialLayerState.projectLayers as AddedProjectLayer[]
    : []
  const persistedLayerOrder = Array.isArray(initialLayerState.layerOrder)
    ? initialLayerState.layerOrder as string[]
    : ["annotations", "osm"]
  const persistedOsmVisible = typeof initialLayerState.osmLayerVisible === "boolean" ? initialLayerState.osmLayerVisible : true
  const persistedAnnotationVisible = typeof initialLayerState.annotationLayerVisible === "boolean" ? initialLayerState.annotationLayerVisible : true
  const persistedOsmOpacity = typeof initialLayerState.osmLayerOpacity === "number" ? initialLayerState.osmLayerOpacity : 1
  const persistedAnnotationOpacity = typeof initialLayerState.annotationLayerOpacity === "number" ? initialLayerState.annotationLayerOpacity : 1

  const [classes, setClasses] = React.useState<PaintClass[]>([])
  const [selectedClassId, setSelectedClassId] = React.useState<number | null>(null)
  const [activeTool, setActiveTool] = React.useState<ToolMode>(ToolMode.Cursor)
  const [paintStyle, setPaintStyle] = React.useState<PaintStyle>(PaintStyle.Polygon)
  const [snapToBoundaryEnabled, setSnapToBoundaryEnabled] = React.useState(true)
  const [updateVerticesOnOverlapEnabled, setUpdateVerticesOnOverlapEnabled] = React.useState(false)
  const [opacity, setOpacity] = React.useState<number>(persistedAnnotationOpacity)
  const [canUndo, setCanUndo] = React.useState(false)
  const [canRedo, setCanRedo] = React.useState(false)
  const [currentAnnotationName, setCurrentAnnotationName] = React.useState<string>(annotationName)
  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false)
  const [isSaving, setIsSaving] = React.useState(false)
  const [isLoadingGeoJSON, setIsLoadingGeoJSON] = React.useState(false)
  const [showLoadDataModal, setShowLoadDataModal] = React.useState(false)
  const [layerPaletteVisible, setLayerPaletteVisible] = React.useState(false)
  const [savedDatasets, setSavedDatasets] = React.useState<CompiledDatasetRecord[]>([])
  const [isLoadingSavedDatasets, setIsLoadingSavedDatasets] = React.useState(false)
  const [savedDatasetsError, setSavedDatasetsError] = React.useState<string | null>(null)
  const [isImportingDatasetId, setIsImportingDatasetId] = React.useState<number | null>(null)
  const [importProgress, setImportProgress] = React.useState<number>(0)
  const [importStatusMessage, setImportStatusMessage] = React.useState<string>("")
  const [projectLayers, setProjectLayers] = React.useState<AddedProjectLayer[]>(persistedProjectLayers)
  const [layerOrder, setLayerOrder] = React.useState<string[]>(persistedLayerOrder)
  const [dbModels, setDbModels] = React.useState<DBModels>({ mapTileLayers: [], overlays: [] })
  const [osmLayerVisible, setOsmLayerVisible] = React.useState(persistedOsmVisible)
  const [annotationLayerVisible, setAnnotationLayerVisible] = React.useState(persistedAnnotationVisible)
  const [osmLayerOpacity, setOsmLayerOpacity] = React.useState(persistedOsmOpacity)
  const [annotationLayerOpacity, setAnnotationLayerOpacity] = React.useState(persistedAnnotationOpacity)
  const [projectLayerStats, setProjectLayerStats] = React.useState<Record<string, tileGridStats>>({})
  const [localGeotiffLayers, setLocalGeotiffLayers] = React.useState<Record<string, LocalGeotiffLayerState>>({})
  const mapRef = React.useRef<PaintbrushMapRef>(null)
  const previousToolRef = React.useRef<ToolMode>(ToolMode.Cursor)
  const spacebarPressedRef = React.useRef(false)

  // Update local name when prop changes (e.g., after save)
  React.useEffect(() => {
    setCurrentAnnotationName(annotationName)
    setHasUnsavedChanges(false)
  }, [annotationName])

  React.useEffect(() => {
    const headers = new Headers()
    const csrfToken = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content
    if (csrfToken) headers.set("X-CSRF-Token", csrfToken)

    Promise.all([
      fetch(`/teams/${teamId}/map_tile_layers.json`, { headers }).then(response => response.ok ? response.json() : []),
      fetch(`/teams/${teamId}/overlays.json`, { headers }).then(response => response.ok ? response.json() : [])
    ]).then(([mapTileLayers, overlays]) => {
      setDbModels({
        mapTileLayers: Array.isArray(mapTileLayers) ? mapTileLayers : [],
        overlays: Array.isArray(overlays) ? overlays : []
      })
    }).catch(error => {
      console.warn("Unable to load map tile layers / overlays for paintbrush palette", error)
    })
  }, [teamId])

  React.useEffect(() => {
    if (!showLoadDataModal) return

    let mounted = true
    setIsLoadingSavedDatasets(true)
    setSavedDatasetsError(null)
    getDatasets(teamId)
      .then((datasets) => {
        if (!mounted) return
        const filteredDatasets = datasets.filter((dataset) => {
          const gridType = (dataset.gridtype || "").toLowerCase()
          return gridType.includes("boolean") || gridType.includes("categorical")
        })
        setSavedDatasets(filteredDatasets.slice().sort((a, b) => a.name.localeCompare(b.name)))
      })
      .catch((error) => {
        console.error("Error loading saved datasets for Paintbrush:", error)
        if (!mounted) return
        setSavedDatasets([])
        setSavedDatasetsError("Unable to load saved datasets.")
      })
      .finally(() => {
        if (mounted) setIsLoadingSavedDatasets(false)
      })

    return () => {
      mounted = false
    }
  }, [showLoadDataModal, teamId])

  React.useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "team-data-modal-close") {
        setShowLoadDataModal(false)
      }
    }

    window.addEventListener("message", handleMessage)
    return () => window.removeEventListener("message", handleMessage)
  }, [])

  React.useEffect(() => {
    const projectLayerIds = new Set(projectLayers.map(entry => entry.id))
    setProjectLayerStats(previous => {
      const next: Record<string, tileGridStats> = {}
      projectLayers.forEach(entry => {
        if (previous[entry.id]) next[entry.id] = previous[entry.id]
      })
      return next
    })
    setLayerOrder(previousOrder => {
      const next = previousOrder.filter(
        id =>
          id === "annotations" ||
          id === "osm" ||
          projectLayerIds.has(id) ||
          parsePaintbrushLocalGeotiffOrderId(id) !== null
      )
      if (!next.includes("annotations")) next.unshift("annotations")
      const osmIdx = next.indexOf("osm")
      const missing = projectLayers.map(entry => entry.id).filter(id => !next.includes(id))
      if (missing.length) {
        if (osmIdx >= 0) {
          next.splice(osmIdx, 0, ...missing)
        } else {
          const annIdx = next.indexOf("annotations")
          if (annIdx >= 0) next.splice(annIdx + 1, 0, ...missing)
          else next.unshift(...missing)
        }
      }
      return next
    })
  }, [projectLayers])

  const selectedClass = React.useMemo(
    () => classes.find(paintClass => paintClass.id === selectedClassId) ?? null,
    [classes, selectedClassId]
  )

  // Hold Space to temporarily switch to the Paint tool; release to go back.
  React.useEffect(() => {
    const isTypingTarget = (target: EventTarget | null) => {
      const el = target as HTMLElement | null
      if (!el) return false
      return el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== " " && event.key !== "Spacebar") return
      if (event.repeat) {
        event.preventDefault()
        return
      }
      if (isTypingTarget(event.target)) return
      if (spacebarPressedRef.current) return
      if (!selectedClass) return

      event.preventDefault()
      spacebarPressedRef.current = true
      previousToolRef.current = activeTool
      if (activeTool !== ToolMode.Paint) {
        setActiveTool(ToolMode.Paint)
      }
    }

    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.key !== " " && event.key !== "Spacebar") return
      if (!spacebarPressedRef.current) return
      event.preventDefault()
      spacebarPressedRef.current = false
      if (previousToolRef.current !== ToolMode.Paint) {
        setActiveTool(previousToolRef.current)
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    window.addEventListener("keyup", handleKeyUp)
    return () => {
      window.removeEventListener("keydown", handleKeyDown)
      window.removeEventListener("keyup", handleKeyUp)
    }
  }, [activeTool, selectedClass])

  const handleUndo = () => {
    mapRef.current?.undo()
  }

  const handleRedo = () => {
    mapRef.current?.redo()
  }

  const handleSave = async () => {
    const csrfToken = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content
    if (!csrfToken) {
      alert('CSRF token not found. Please refresh the page and try again.')
      return
    }

    const headers = new Headers()
    headers.set('X-CSRF-Token', csrfToken)

    // Save annotation name if it has changed
    if (hasUnsavedChanges) {
      setIsSaving(true)
      const method = 'PATCH'
      headers.set('X-CSRF-Token', csrfToken)

      const body = new FormData()
      body.set('annotation[name]', currentAnnotationName)

      const url = `/paintbrush/${annotationId}/edit_annotation`
      
      try {
        const response = await fetch(url, { 
          method, 
          headers, 
          body,
          redirect: 'manual'
        })

        if (response.status === 0 || response.status === 200 || (response.status >= 200 && response.status < 400)) {
          const data = await response.json().catch(() => ({}))
          if (typeof data.name === "string" && data.name !== currentAnnotationName) {
            setCurrentAnnotationName(data.name)
          }
          setHasUnsavedChanges(false)
        } else {
          const errorText = await response.text().catch(() => response.statusText)
          throw new Error(`HTTP ${response.status}: ${errorText}`)
        }
      } catch (error) {
        console.error('Error saving annotation name:', error)
        alert(`An error occurred while saving the annotation name: ${error instanceof Error ? error.message : 'Unknown error'}`)
        setIsSaving(false)
        return
      } finally {
        setIsSaving(false)
      }
    }

    // Save polygons
    const geoJson = mapRef.current?.exportGeoJSON()
    const layerState = {
      projectLayers,
      layerOrder: omitPaintbrushLocalGeotiffsFromLayerOrder(layerOrder),
      osmLayerVisible,
      annotationLayerVisible,
      osmLayerOpacity,
      annotationLayerOpacity
    }

    if (geoJson || Object.keys(layerState).length > 0) {
      const method = 'PATCH'
      const body = new FormData()
      if (geoJson) body.set('geojson', geoJson)
      body.set('layer_state', JSON.stringify(layerState))

      try {
        const response = await fetch(`/paintbrush/${annotationId}/save_geojson`, { 
          method, 
          headers, 
          body,
          redirect: 'manual'
        })

        if (response.status === 0 || response.status === 200 || (response.status >= 200 && response.status < 400)) {
          // Save successful - no alert needed
          broadcastUpdate()
        } else {
          const errorText = await response.text().catch(() => response.statusText)
          throw new Error(`HTTP ${response.status}: ${errorText}`)
        }
      } catch (error) {
        console.error('Error saving polygons:', error)
        alert(`An error occurred while saving polygons: ${error instanceof Error ? error.message : 'Unknown error'}`)
      }
    }
  }

  const handleNameChange = (newName: string) => {
    setCurrentAnnotationName(newName)
    setHasUnsavedChanges(newName !== annotationName)
  }

  const handleDownloadGeoJSON = React.useCallback(() => {
    const geoJson = mapRef.current?.exportGeoJSON()
    if (!geoJson) {
      alert("No GeoJSON data available to download yet.")
      return
    }

    const blob = new Blob([geoJson], { type: "application/geo+json" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    const safeName = (currentAnnotationName || `paintbrush-${annotationId}`)
      .trim()
      .replace(/[^a-z0-9-_]+/gi, "_")
      .replace(/^_+|_+$/g, "")
    link.href = url
    link.download = `${safeName || `paintbrush-${annotationId}`}.geojson`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }, [annotationId, currentAnnotationName])

  const handleLoadData = React.useCallback(() => {
    setShowLoadDataModal(true)
  }, [])

  const loadDatasetRecord = React.useCallback((datasetId: number) => {
    return new Promise<BooleanTileGrid | CategoricalTileGrid>((resolve, reject) => {
      getDataset(datasetId, teamId, (error: Error | null, dataset: unknown) => {
        if (error) {
          reject(error)
          return
        }
        if (dataset instanceof BooleanTileGrid || dataset instanceof CategoricalTileGrid) {
          resolve(dataset)
          return
        }
        reject(new Error("Selected dataset must be Boolean or Categorical."))
      })
    })
  }, [teamId])

  const handleImportSavedDataset = React.useCallback(async (dataset: CompiledDatasetRecord) => {
    if (!mapRef.current) return
    setIsImportingDatasetId(dataset.id)
    setImportProgress(5)
    setImportStatusMessage(`Preparing ${dataset.name}...`)
    try {
      const grid = await loadDatasetRecord(dataset.id)
      setImportProgress(20)
      setImportStatusMessage("Dataset loaded. Building class map...")

      let classByValue: Record<number, { classId: number; color: string }> = {}

      if (grid instanceof BooleanTileGrid) {
        const classId = Date.now()
        classByValue[1] = { classId, color: DEFAULT_CLASS_PALETTE[0] }
        setClasses([{
          id: classId,
          name: dataset.name || "Imported Boolean",
          color: DEFAULT_CLASS_PALETTE[0],
          opacity: 1
        }])
        setSelectedClassId(classId)
      } else {
        classByValue = buildImportClassByValueFromGrid(grid, dataset.name, classes)

        if (Object.keys(classByValue).length === 0) {
          alert("No matching paint classes found. Add the dataset as a layer and use \"Load in classes\", or create classes with matching names first.")
          return
        }
      }
      setImportProgress(35)
      setImportStatusMessage("Converting raster to editable vectors...")

      if (grid instanceof CategoricalTileGrid) {
        const tileGridJSON = grid.toJSON()
        const formData = new FormData()
        const jsonBlob = new Blob([JSON.stringify(tileGridJSON)], { type: "application/json" })
        formData.append("dataset", jsonBlob, `${dataset.name}.json`)
        setImportProgress(45)
        setImportStatusMessage("Uploading dataset for GeoTIFF conversion...")

        const geotiffResponse = await fetch(`${LANDSCAPES_V2_API}/geotiff`, {
          method: "POST",
          body: formData
        })
        if (!geotiffResponse.ok) {
          throw new Error(`GeoTIFF conversion failed (${geotiffResponse.status})`)
        }

        const geotiffBlob = await geotiffResponse.blob()
        const shpForm = new FormData()
        shpForm.append("geotiff", geotiffBlob, `${dataset.name}.tif`)
        setImportProgress(60)
        setImportStatusMessage("GeoTIFF ready. Requesting shapefile conversion...")
        const clrContent = generateClrFromClassMap(
          tileGridJSON.labels as Record<string, unknown> | undefined,
          classByValue
        )
        if (clrContent.trim().length > 0) {
          shpForm.append("clr", new Blob([clrContent], { type: "text/plain" }), `${dataset.name}.clr`)
        }
        shpForm.append("smooth", String(GEOTIFF_TO_SHP_SMOOTH))
        shpForm.append("smooth_iters", String(GEOTIFF_TO_SHP_SMOOTH_ITERS))

        const shpResponse = await fetch(`${LANDSCAPES_V2_API}/geotiff_to_shp`, {
          method: "POST",
          body: shpForm
        })
        if (!shpResponse.ok) {
          throw new Error(`Shapefile conversion failed (${shpResponse.status})`)
        }

        setImportProgress(75)
        setImportStatusMessage("Downloading converted vectors...")
        const zipArrayBuffer = await shpResponse.arrayBuffer()
        setImportProgress(85)
        setImportStatusMessage("Parsing shapefile package...")
        const parsedGeoJson = await parseShapefileZip(zipArrayBuffer)
        setImportProgress(95)
        setImportStatusMessage("Importing features into Paintbrush...")
        mapRef.current.importGeoJSONFeatures(parsedGeoJson, classByValue)
      } else {
        setImportProgress(80)
        setImportStatusMessage("Importing boolean vectors into Paintbrush...")
        mapRef.current.importTileGridAsVectors(grid, classByValue)
      }

      setImportProgress(100)
      setImportStatusMessage("Import complete.")
      setShowLoadDataModal(false)
    } catch (error) {
      console.error("Failed to import dataset into Paintbrush:", error)
      alert(`Failed to import dataset: ${error instanceof Error ? error.message : "Unknown error"}`)
    } finally {
      setIsImportingDatasetId(null)
      setTimeout(() => {
        setImportProgress(0)
        setImportStatusMessage("")
      }, 300)
    }
  }, [classes, loadDatasetRecord])

  const handleProjectLayerStatsChange = React.useCallback((layerId: string, stats: tileGridStats) => {
    setProjectLayerStats(previous => {
      const current = previous[layerId]
      if (
        current &&
        current.min === stats.min &&
        current.max === stats.max &&
        current.type === stats.type &&
        current.zoom === stats.zoom &&
        current.area === stats.area &&
        current.length === stats.length &&
        JSON.stringify(current.labels ?? null) === JSON.stringify(stats.labels ?? null) &&
        JSON.stringify(current.props ?? null) === JSON.stringify(stats.props ?? null)
      ) {
        return previous
      }
      return { ...previous, [layerId]: stats }
    })
  }, [])

  const handleDatasetLayerStyleMetadataChange = React.useCallback(
    (layerId: string, updates: Partial<DatasetLayer>) => {
      setProjectLayers(previous =>
        previous.map(entry =>
          entry.id === layerId && entry.layer.type === "DatasetLayer"
            ? { ...entry, layer: { ...entry.layer, ...updates } }
            : entry
        )
      )
    },
    []
  )

  const handleLocalGeotiffsParsed = React.useCallback((items: LocalGeotiffLayerState[]) => {
    setLocalGeotiffLayers(prev => {
      const next = { ...prev }
      for (const it of items) {
        next[it.id] = it
      }
      return next
    })
    setLayerOrder(prev => {
      const additions = items.map(it => paintbrushLocalGeotiffOrderId(it.id))
      const osmIndex = prev.indexOf("osm")
      if (osmIndex === -1) {
        return [...prev, ...additions]
      }
      const copy = [...prev]
      copy.splice(osmIndex, 0, ...additions)
      return copy
    })
  }, [])

  return (
    <>
      <div style={{ 
        display: "flex", 
        flexDirection: "column", 
        width: "100%", 
        height: "100%",
        minHeight: 0,
        overflow: "hidden"
      }}>
        <PaintbrushToolbar 
          onUndo={handleUndo}
          onRedo={handleRedo}
          canUndo={canUndo}
          canRedo={canRedo}
          backButtonPath={backButtonPath}
          annotationName={currentAnnotationName}
          onAnnotationNameChange={handleNameChange}
          onSave={handleSave}
          onDownload={handleDownloadGeoJSON}
          hasUnsavedChanges={hasUnsavedChanges}
          isSaving={isSaving}
        />
        <div style={{ display: "flex", flex: 1, overflow: "hidden", minHeight: 0 }}>
          <div style={{ flex: 1, overflow: "hidden", minHeight: 0, minWidth: 0 }}>
            <PaintbrushMap 
              ref={mapRef}
              activeTool={activeTool} 
              paintStyle={paintStyle}
              selectedClass={selectedClass} 
              opacity={opacity} 
              classes={classes}
              snapToBoundaryEnabled={snapToBoundaryEnabled}
              updateVerticesOnOverlapEnabled={updateVerticesOnOverlapEnabled}
              osmLayerVisible={osmLayerVisible}
              annotationLayerVisible={annotationLayerVisible}
              osmLayerOpacity={osmLayerOpacity}
              annotationLayerOpacity={annotationLayerOpacity}
              projectLayers={projectLayers}
              layerOrder={layerOrder}
              localGeotiffLayers={localGeotiffLayers}
              onLocalGeotiffsParsed={handleLocalGeotiffsParsed}
              dbModels={dbModels}
              teamId={teamId}
              geojsonUrl={geojsonUrl}
              onUndoRedoStateChange={(undo, redo) => {
                setCanUndo(undo)
                setCanRedo(redo)
              }}
              onDataChange={() => setHasUnsavedChanges(true)}
              onClassesRestored={(restoredClasses) => {
                setClasses(restoredClasses)
              }}
              onLoadingChange={(isLoading) => {
                setIsLoadingGeoJSON(isLoading)
              }}
              onProjectLayerStatsChange={handleProjectLayerStatsChange}
              onDatasetLayerStyleMetadataChange={handleDatasetLayerStyleMetadataChange}
            />
          </div>
          {layerPaletteVisible && (
            <LayerPalette
              hide={() => setLayerPaletteVisible(false)}
              addLayer={(layer: Layer) => {
                const newId = `project-layer-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
                setProjectLayers(prev => [...prev, { id: newId, layer }])
                setLayerOrder(prev => {
                  const osmIndex = prev.indexOf("osm")
                  if (osmIndex === -1) return [...prev, newId]
                  const copy = [...prev]
                  copy.splice(osmIndex, 0, newId)
                  return copy
                })
                setLayerPaletteVisible(false)
              }}
              dbModels={dbModels}
              getTeamDatasets={() => getDatasets(teamId)}
              getTeamDataLayers={() => getTeamData(teamId)}
              teamId={teamId}
              teamName={teamName}
              permissions={permissions}
            />
          )}
          <PaintbrushSidebar
            classes={classes}
            setClasses={setClasses}
            selectedClassId={selectedClassId}
            setSelectedClassId={setSelectedClassId}
            activeTool={activeTool}
            setActiveTool={setActiveTool}
            paintStyle={paintStyle}
            setPaintStyle={setPaintStyle}
            snapToBoundaryEnabled={snapToBoundaryEnabled}
            setSnapToBoundaryEnabled={setSnapToBoundaryEnabled}
            updateVerticesOnOverlapEnabled={updateVerticesOnOverlapEnabled}
            setUpdateVerticesOnOverlapEnabled={setUpdateVerticesOnOverlapEnabled}
            opacity={opacity}
            setOpacity={setOpacity}
            isLoadingGeoJSON={isLoadingGeoJSON}
            projectLayers={projectLayers}
            setProjectLayers={setProjectLayers}
            layerOrder={layerOrder}
            setLayerOrder={setLayerOrder}
            localGeotiffLayers={localGeotiffLayers}
            setLocalGeotiffLayers={setLocalGeotiffLayers}
            showLayerPalette={() => setLayerPaletteVisible(true)}
            osmLayerVisible={osmLayerVisible}
            setOsmLayerVisible={setOsmLayerVisible}
            annotationLayerVisible={annotationLayerVisible}
            setAnnotationLayerVisible={setAnnotationLayerVisible}
            osmLayerOpacity={osmLayerOpacity}
            setOsmLayerOpacity={setOsmLayerOpacity}
            annotationLayerOpacity={annotationLayerOpacity}
            setAnnotationLayerOpacity={setAnnotationLayerOpacity}
            annotationId={annotationId}
            geojsonUrl={geojsonUrl}
            projectLayerStats={projectLayerStats}
            onLoadData={handleLoadData}
          />
        </div>
      </div>
      {showLoadDataModal && (
        <div
          className="modal d-block"
          role="dialog"
          aria-modal="true"
          aria-labelledby="paintbrushLoadDataModalTitle"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.4)" }}
          onClick={() => setShowLoadDataModal(false)}
        >
          <div
            className="modal-dialog modal-xl modal-dialog-centered"
            role="document"
            onClick={event => event.stopPropagation()}
          >
            <div className="modal-content" style={{ minHeight: "75vh" }}>
              <div className="modal-header">
                <h5 className="modal-title" id="paintbrushLoadDataModalTitle">Load Data</h5>
                <button type="button" className="close" aria-label="Close" onClick={() => setShowLoadDataModal(false)}>
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div className="modal-body d-flex flex-column" style={{ maxHeight: "75vh", overflow: "auto" }}>
                <p className="text-muted mb-3">
                  Select a saved dataset to load into Paintbrush.
                </p>
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <h6 className="mb-0">Saved Datasets</h6>
                  <small className="text-muted">{savedDatasets.length} found</small>
                </div>
                {isImportingDatasetId !== null && (
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <small className="text-muted">{importStatusMessage || "Importing dataset..."}</small>
                      <small className="text-muted">{Math.round(importProgress)}%</small>
                    </div>
                    <div className="progress" style={{ height: "8px" }}>
                      <div
                        className="progress-bar progress-bar-striped progress-bar-animated"
                        role="progressbar"
                        style={{ width: `${importProgress}%` }}
                        aria-valuenow={importProgress}
                        aria-valuemin={0}
                        aria-valuemax={100}
                      />
                    </div>
                  </div>
                )}
                {isLoadingSavedDatasets && (
                  <div className="text-muted">
                    <i className="fas fa-spinner fa-spin mr-2" />
                    Loading saved datasets...
                  </div>
                )}
                {savedDatasetsError && (
                  <div className="alert alert-warning py-2 px-3 mb-2" role="alert">
                    {savedDatasetsError}
                  </div>
                )}
                {!isLoadingSavedDatasets && !savedDatasetsError && (
                  <>
                    {savedDatasets.length === 0 ? (
                      <div className="text-muted small">No saved datasets found yet.</div>
                    ) : (
                      <div className="list-group">
                        {savedDatasets.map((dataset) => (
                            <button
                              key={dataset.id}
                              type="button"
                              className="list-group-item list-group-item-action d-flex align-items-center justify-content-between"
                              onClick={() => handleImportSavedDataset(dataset)}
                              disabled={isImportingDatasetId !== null}
                            >
                              <div className="d-flex align-items-center">
                                <i className="fas fa-database mr-3 text-primary" />
                                <div className="text-left">
                                  <div className="font-weight-bold">{dataset.name}</div>
                                  <small className="text-muted">Team #{dataset.team_id}</small>
                                </div>
                              </div>
                              <span className="badge badge-light text-muted">
                                {isImportingDatasetId === dataset.id ? "Importing..." : dataset.gridtype}
                              </span>
                            </button>
                          ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

